import { useCallback } from 'react';

import type { BreadcrumbItem, BreadcrumbSibling } from '@/components/Breadcrumb/Breadcrumb';

import type { Permission } from './permissions.catalog';
import type { AccessVerdict } from './plan-access';
import { useAccessGate } from './useAccessGate';

/** A switcher row plus the rights its destination's own route guard opens on, any-of. */
export type GatedSibling = BreadcrumbSibling & { anyOf?: readonly Permission[] };

export interface BreadcrumbGate {
  /** `open` for a destination naming no rights — it requires none. */
  verdictFor: (anyOf?: readonly Permission[]) => AccessVerdict;
  gateCrumb: (crumb: BreadcrumbItem, anyOf?: readonly Permission[]) => BreadcrumbItem;
  gateSiblings: (rows: GatedSibling[]) => BreadcrumbSibling[];
  gateParent: (
    parent: BreadcrumbSibling | undefined,
    anyOf?: readonly Permission[],
  ) => BreadcrumbSibling | undefined;
}

/**
 * The breadcrumb's half of the plan gate: what each rung of the trail, and each row of the
 * leaf's switcher, does about a destination this viewer may not be able to reach.
 *
 * Same three verdicts the nav and the route guards read, and the same split between them
 * (see `AccessVerdict`) — only the rendering differs, because a crumb is not a nav item:
 *
 * - `open`   — untouched.
 * - `locked` — the owner's plan does not carry it, but an upgrade would. Padlocked and
 *              still followable: the destination is the placeholder that sells the upgrade,
 *              which is somewhere worth going. Matches the nav, the bottom bar and the
 *              Discover cards.
 * - `hidden` — nothing to say and nothing to buy. A switcher row is dropped outright. A
 *              *crumb* keeps its label but loses its click, because a crumb is the trail
 *              that says where you are: deleting a rung of it would misdescribe the page,
 *              while leaving it clickable would promise a destination whose guard answers
 *              with a redirect to the dashboard.
 *
 * The rights to pass are the destination's own, from `route-rights` — one list per
 * destination, shared with the guard that will be asked the same question.
 */
export const useBreadcrumbGate = (): BreadcrumbGate => {
  const { anyVerdictFor } = useAccessGate();

  const verdictFor = useCallback(
    (anyOf?: readonly Permission[]): AccessVerdict => anyVerdictFor(anyOf ?? []),
    [anyVerdictFor],
  );

  const gateCrumb = useCallback(
    (crumb: BreadcrumbItem, anyOf?: readonly Permission[]): BreadcrumbItem => {
      const verdict = verdictFor(anyOf);
      if (verdict === 'open') return crumb;
      if (verdict === 'locked') return { ...crumb, locked: true };
      return { ...crumb, onClick: undefined };
    },
    [verdictFor],
  );

  const gateSiblings = useCallback(
    (rows: GatedSibling[]): BreadcrumbSibling[] =>
      rows.reduce<BreadcrumbSibling[]>((kept, { anyOf, ...row }) => {
        const verdict = verdictFor(anyOf);
        // The row you are standing on stays, whatever the verdict says: the switcher has to
        // be able to mark the current section, and a page that rendered is a page reached.
        if (verdict === 'hidden' && !row.current) return kept;
        kept.push(verdict === 'locked' ? { ...row, locked: true } : row);
        return kept;
      }, []),
    [verdictFor],
  );

  const gateParent = useCallback(
    (parent: BreadcrumbSibling | undefined, anyOf?: readonly Permission[]) => {
      if (!parent) return undefined;
      const verdict = verdictFor(anyOf);
      // The ascent row is nothing but its click, so a destination out of reach leaves it
      // with no reason to be drawn.
      if (verdict === 'hidden') return undefined;
      return verdict === 'locked' ? { ...parent, locked: true } : parent;
    },
    [verdictFor],
  );

  return { verdictFor, gateCrumb, gateSiblings, gateParent };
};
